import { T, useGT } from 'gt-react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MultiItemsToggle } from './CatalogItemNumbersInput';

/**
 * The catalog search input, lifted out of the table's toolbar so a page can put
 * the result count beside it and stack the advanced-filters panel below. Mirrors
 * the built-in search markup in `GentlyTable`.
 *
 * `regex` wires the VS Code-style `.*` toggle; omit it where the term is matched
 * as a plain substring (the review page searches one AS/400 column). `invalid`
 * flags a pattern the search rejected.
 *
 * `multiItems` wires the "Multiple items" mode switch beside the regex toggle.
 * This component is the single-term half of that pair — when the mode is on,
 * the caller swaps it for `CatalogItemNumbersInput`, which renders the same
 * toggle in the same spot.
 */
export function CatalogSearchBox({
  value,
  onChange,
  placeholder,
  regex,
  invalid = false,
  invalidMessage,
  multiItems,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  regex?: { active: boolean; onToggle: () => void };
  invalid?: boolean;
  invalidMessage?: string;
  multiItems?: { onToggle: () => void };
}) {
  const gt = useGT();
  const trailingControls = (regex ? 1 : 0) + (multiItems ? 1 : 0);
  const paddingRight =
    trailingControls === 2 ? 'pr-14' : trailingControls === 1 ? 'pr-9' : 'pr-3';
  return (
    <div className="relative w-full max-w-sm">
      <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={
          placeholder ??
          gt('Search', {
            context: 'search placeholder on item catalog page',
          })
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={invalid ? invalidMessage : undefined}
        aria-invalid={invalid}
        className={`pl-8 ${paddingRight}${invalid ? ' border-destructive focus-visible:ring-destructive' : ''}`}
      />
      <span className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
        {regex && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={regex.onToggle}
                aria-pressed={regex.active}
                aria-label={gt('Toggle regular expression search', {
                  context: 'table search regex toggle',
                })}
                className={`rounded px-1 py-0.5 font-mono text-xs leading-none transition-colors${regex.active ? ' bg-primary/15 text-primary' : ' text-muted-foreground hover:bg-muted'}`}
              >
                .*
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <T context="table search regex toggle tooltip">
                Toggle regular expression search
              </T>
            </TooltipContent>
          </Tooltip>
        )}
        {multiItems && (
          <MultiItemsToggle active={false} onToggle={multiItems.onToggle} />
        )}
      </span>
    </div>
  );
}
